import { useCallback, useEffect, useMemo } from "react";
import { Box, Dialog, DialogContent } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Formik, Form } from "formik";
import { useAppSelector } from "src/hooks";
import { authAPI, authSelector } from "src/modules/auth/redux/authSlice";
import { loginSchema } from "../schema";
import { FormInput, FormCheckbox, Splashscreen, Logo } from "src/components";
import { generateParaplanLoginQuery } from "../helpers";
import { Credentials } from "../types";
import { PassioService } from "src/types";
import { enqueueSnackbar } from "notistack";

const LoginModal = () => {
    const { tokenIsValid, token, user, savedLogin, service } =
        useAppSelector(authSelector);

    const [login, { isLoading }] = authAPI.useLoginMutation();
    const [passioLogin, { isLoading: passioLoading }] =
        authAPI.usePassioLoginMutation();

    const handleRefresh = useCallback(
        async (values: Credentials, resume = false) => {
            try {
                const isNavigator = service === PassioService.navigator;
                const request =
                    isNavigator || resume // no need to rehash credentials when resuming Paraplan, skip entirely for navigator
                        ? values
                        : {
                              ...generateParaplanLoginQuery(values),
                              autoLogin: values.autoLogin,
                          };

                if (isNavigator) {
                    await passioLogin(request).unwrap();
                } else {
                    await login(request).unwrap();
                }

                enqueueSnackbar("Session restored", {
                    variant: "success",
                });
            } catch (error) {
                enqueueSnackbar(
                    "Login failed. Please check your password/PIN",
                    {
                        variant: "error",
                    }
                );
            }
        },
        [login, passioLogin, service]
    );

    const open = useMemo(
        () => !tokenIsValid && token.length > 0,
        [tokenIsValid, token]
    );

    // if user has autologin credentials saved. proceed to refresh token
    useEffect(() => {
        if (!open) return;
        if (!(savedLogin && savedLogin?.email && savedLogin?.password)) return;
        handleRefresh(savedLogin, true);
    }, [savedLogin, handleRefresh, tokenIsValid, open]);

    if (isLoading) {
        return (
            <Splashscreen
                description="Auto login enabled, we are restoring your session"
                sx={{ position: "fixed", inset: 0, zIndex: 9999 }}
            />
        );
    }

    return (
        <Dialog open={open} fullWidth maxWidth="xs">
            <Formik
                initialValues={{
                    email: user.Email,
                    password: "",
                    autoLogin: true,
                }}
                validationSchema={loginSchema}
                onSubmit={(values) => handleRefresh(values)}
            >
                <DialogContent>
                    <Logo />.
                    <Box component={Form} sx={{ mt: 1 }}>
                        <FormInput
                            fullWidth
                            name="email"
                            label="Email address"
                            margin="normal"
                        />
                        <FormInput
                            fullWidth
                            name="password"
                            label="Password or Pin"
                            type="password"
                            margin="normal"
                        />
                        <FormCheckbox label="Remember me" name="autoLogin" />
                        <LoadingButton
                            size="large"
                            type="submit"
                            fullWidth
                            variant="contained"
                            loading={isLoading}
                            sx={{ my: 2, py: 1, color: "primary.contrastText" }}
                        >
                            {isLoading || passioLoading
                                ? "Verifying account"
                                : "Sign In"}
                        </LoadingButton>
                    </Box>
                </DialogContent>
            </Formik>
        </Dialog>
    );
};

export default LoginModal;
